import { useMemo } from "react";
import { Cell, Pie, PieChart } from "recharts";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "./chart";
import type { ChartConfig } from "./chart";
import { cn } from "../lib/utils";

const DEFAULT_COLORS = ["#655560", "#1564BF", "#405F90", "#8884d8", "#82ca9d", "#ffc658", "#ff7c43", "#a05195"];

export interface ChartPieDataItem {
  name: string;
  value: number;
}

export interface ChartPieProps {
  data: ChartPieDataItem[];
  colors?: string[];
  className?: string;
  showLabels?: boolean;
  showLegend?: boolean;
  height?: number;
}

export const ChartPie = ({
  data,
  colors = DEFAULT_COLORS,
  className,
  showLabels = true,
  showLegend = true,
  height = 300,
}: ChartPieProps) => {
  const chartConfig = useMemo(() => {
    return data.reduce<ChartConfig>((acc, item, index) => {
      acc[item.name] = {
        label: item.name,
        color: colors[index % colors.length],
      };
      return acc;
    }, {});
  }, [data, colors]);

  const dataWithFill = useMemo(() => {
    return data.map((item, index) => ({
      ...item,
      fill: colors[index % colors.length],
    }));
  }, [data, colors]);

  return (
    <ChartContainer config={chartConfig} className={cn("aspect-square", className)} style={{ height }}>
      <PieChart>
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              formatter={(value: number | string, name: string) => (
                <div className="flex items-center justify-between gap-4">
                  <span>{name}</span>
                  <span className="font-mono font-medium">
                    {typeof value === "number" ? value.toLocaleString() : value}
                  </span>
                </div>
              )}
            />
          }
        />
        <Pie
          data={dataWithFill}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={0}
          outerRadius="80%"
          label={
            showLabels
              ? ({ name, percent }: { name?: string; percent?: number }) =>
                  `${name ?? ""} (${((percent ?? 0) * 100).toFixed(0)}%)`
              : false
          }
          labelLine={showLabels}
        >
          {dataWithFill.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Pie>
        {showLegend && <ChartLegend content={<ChartLegendContent nameKey="name" />} />}
      </PieChart>
    </ChartContainer>
  );
};
